import {
  PartnerIntegrations,
  ShowNotificationFunctionProps,
  usePartnerIntegrationWidgetConfigurations,
} from 'lib-integrations-partner-widgets';
import { useHasAccess } from 'lib-shared-access-control';
import { config } from 'lib-shared-backend-config';
import {
  MetaConversation,
  NotificationPayloadT,
} from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';

import { notificationActions } from 'components/notifications/actions/notification';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { usePartnerWidgetFeatureFlags } from 'utils/partnerIntegrationsWidget/usePartnerWidgetFeatureFlags';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage';

import { CustomCards } from '../../cards/customCards/customCards';
import { useCardSectionVisibility } from '../../hooks/useCardSectionVisibility';
import { useCustomizationSnapshot } from '../../hooks/useCustomizationSnapshot';
import { useSidebarMode } from '../../hooks/useSidebarMode';
import { partnerIntegrationNotificationTimeout } from '../../partnerIntegrations';
import { reorderNumeric } from '../../utils/reorder';
import { CardMoveHandle } from '../cardMoveHandle/cardMoveHandle';
import { CardSection } from '../cardSection/cardSection';
import { VisibilityToggle } from '../visibilityToggle/visibilityToggle';

type IntegrationCardsProps = {
  metaConversation: MetaConversation;
  isMinimized: boolean;
  isCardOpen: (card: string) => boolean;
  toggleCard: (card: string) => void;
  index: number;
};

const type = Symbol('nativeIntegrationCard');

export const IntegrationCards = ({
  metaConversation,
  isMinimized,
  isCardOpen,
  toggleCard,
  index,
}: IntegrationCardsProps): JSX.Element | null => {
  const { hiddenItems } = useCardSectionVisibility();
  const sidebarMode = useSidebarMode();

  const dispatch = useAppDispatch();
  const flags = useFeatureFlag([
    'partner-integrations-widget',
    'conversation-right-sidebar-visibility',
  ]);

  const hasIntegrationAccess = useHasAccess('conversation.integration');
  const partnerWidgetFeatureFlags = usePartnerWidgetFeatureFlags();

  const storage = useServerStorage('user');

  // Skip the partner-integrations query for users without the
  // `conversation.integration` scope: the gateway authorization-check plugin
  // would reject it and the log noise adds up. The early return below still
  // hides the section entirely for those users.
  const { widgets, isLoading } = usePartnerIntegrationWidgetConfigurations(
    config.url.partnerIntegrations,
    {
      skip: !flags.partnerIntegrationsWidget || !hasIntegrationAccess,
    },
  );

  const sortedWidgets = useMemo(
    () =>
      (widgets ?? [])
        .filter(
          (w) =>
            partnerWidgetFeatureFlags[w.type] &&
            w.enabled &&
            (sidebarMode === 'customization' ||
              !hiddenItems.has(w.integrationKey)),
        )
        .sort((a, b) => {
          const cardOrder =
            storage.data.conversation?.sidebarIntegrationOrder ?? {};

          return (
            (cardOrder[a.id] ?? -Infinity) - (cardOrder[b.id] ?? -Infinity) ||
            a.name.localeCompare(b.name)
          );
        }),
    [
      hiddenItems,
      sidebarMode,
      widgets,
      partnerWidgetFeatureFlags,
      storage.data.conversation?.sidebarIntegrationOrder,
    ],
  );

  const { items: preparedWidgets, reorder: reorderWidgets } =
    useCustomizationSnapshot(sortedWidgets, 'integrationKey');

  const showNotification = useCallback(
    ({ title, text, icon, severity }: ShowNotificationFunctionProps) => {
      const time =
        severity === 'warning' || severity === 'danger'
          ? 0
          : partnerIntegrationNotificationTimeout;
      const className = severity as string;

      const payload: NotificationPayloadT = {
        title,
        text,
        icon,
        className,
        time,
      };
      dispatch(notificationActions.notificationAdd(payload));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleMove = useCallback(
    ({ dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number }) => {
      reorderWidgets(dragIndex, hoverIndex);

      storage.set({
        conversation: (draft) => {
          draft.sidebarIntegrationOrder = reorderNumeric(
            preparedWidgets,
            dragIndex,
            hoverIndex,
          );
        },
      });

      ampli.conversationRightSidebarItemReordered({
        sidebarItemType: 'card',
      });
    },
    [storage, preparedWidgets, reorderWidgets],
  );

  if (!hasIntegrationAccess) {
    return null;
  }

  if (
    flags.conversationRightSidebarVisibility &&
    !isLoading &&
    !preparedWidgets.length
  ) {
    return null;
  }

  const sectionId = flags.conversationRightSidebarVisibility
    ? 'nativeIntegration'
    : 'legacyIntegration';

  return (
    <CardSection sectionId={sectionId} index={index} isMinimized={isMinimized}>
      {flags.partnerIntegrationsWidget && (
        <PartnerIntegrations
          configuration={{
            baseUrl: config.url.partnerIntegrations,
          }}
          widgets={preparedWidgets}
          conversationContext={{
            conversationId: metaConversation.conversation?.id || null,
            agent:
              metaConversation?.conversation?.assignment?.agent ?? undefined,
            customer: metaConversation?.requester ?? undefined,
          }}
          showNotification={showNotification}
          onToggleFn={toggleCard}
          isOpenFn={isCardOpen}
          isMinimized={isMinimized}
          renderDragElement={(key, idx, row) => (
            <CardMoveHandle
              visibilityCardId={key}
              index={idx}
              type={type}
              target={row}
              onMove={handleMove}
            />
          )}
          renderVisibilityToggleElement={(key: string) => (
            <VisibilityToggle itemType="card" itemId={key} />
          )}
        />
      )}

      {!flags.conversationRightSidebarVisibility && (
        <CustomCards
          isMinimized={isMinimized}
          csid={metaConversation.conversation?.id}
        />
      )}
    </CardSection>
  );
};
